/* eslint-disable no-underscore-dangle */
import detachTask from 'helpers/detachTask'
import uuidv4 from 'helpers/uuidv4'

import services from './services'
export { services }

const base64 = str => {
  if (typeof btoa === 'function') {
    return btoa(str)
  }
  return Buffer.from(str).toString('base64')
}

/**
 *
 * @type {import('./@types').SendEventType}
 */
export const sendEvent = async (evt, { relay = true } = {}) => {
  const shouldBlockEvent = (await Promise.all(window.__GDM_EVENTS__CALLBACK || []))
    .map(callback => {
      return callback(evt)
    })
    .some(val => {
      return val === false
    })

  if (shouldBlockEvent) {
    console.debug('The event was blocked due an attached callback returned false', { event: evt })
    return
  }
  Object.entries(services).forEach(([, service]) => {
    return service.sendEvent(evt, { relay })
  })
}

const isClientSide = typeof window !== 'undefined'

// https://stackoverflow.com/questions/7616461/generate-a-hash-from-string-in-javascript/7616484#7616484
const hashCode = (str = '') => {
  let hash = 0
  let i
  let chr
  for (i = 0; i < str.length; i += 1) {
    chr = str.charCodeAt(i)
    hash = (hash << 5) - hash + chr
    hash |= 0 // Convert to 32bit integer
  }
  return hash
}

const flat = (obj, prefix = '') => {
  return Object.entries(obj).reduce((acc, [key, value]) => {
    // if key starts with _ then it is a specific service key
    if (key.startsWith('_')) {
      return {
        ...acc,
        [key]: value,
      }
    }

    return {
      ...acc,
      [`${prefix}${key}`]: value,
    }
  }, {})
}

const parseDataset = dataset => {
  return Object.entries(dataset).reduce((acc, [key, value]) => {
    if (!key.startsWith('evt')) {
      return acc
    }
    return {
      ...acc,
      [key.replace('evt', '').toLowerCase()]: value,
    }
  }, {})
}

/**
 *
 * @type {import('./@types').AddEventType}
 */
export const addEvent = (evt, options = {}) => {
  if (!evt.eventGA4Name) {
    evt.eventGA4Name = evt.name
  }

  if (options.noClientSide) {
    return {
      'data-evt-name': evt.name,
      'data-evt-fallback': base64(JSON.stringify(evt)),
    }
  }

  const id = hashCode(JSON.stringify(evt))
  if (isClientSide) {
    window.__GDM_EVENTS__ = window.__GDM_EVENTS__ || {}
    window.__GDM_EVENTS__[id] = flat(evt)
  }

  return {
    ...(evt.isImpressionEvent ? { 'data-ievt-id': id } : { 'data-evt-id': id }),
    'data-evt-name': evt.name,
  }
}

/**
 * @type{import('./@types').AttachSendEventCallbackType}
 */
export const attachSendEventCallback = callback => {
  if (!isClientSide) {
    return () => {}
  }
  window.__GDM_EVENTS__CALLBACK = window.__GDM_EVENTS__CALLBACK || []
  window.__GDM_EVENTS__CALLBACK = window.__GDM_EVENTS__CALLBACK.concat([callback])
  return () => {
    window.__GDM_EVENTS__CALLBACK = window.__GDM_EVENTS__CALLBACK.filter(c => {
      return c !== callback
    })
  }
}

export const flush = () => {
  if (!isClientSide) {
    return
  }
  const currentEvents = Array.from(document.querySelectorAll('[data-evt-id]')).reduce((acc, { dataset }) => {
    return {
      ...acc,
      [dataset.evtId]: true,
    }
  }, {})
  Object.keys(window.__GDM_EVENTS__ || {}).forEach(id => {
    if (!currentEvents[id]) {
      window.__GDM_EVENTS__ = undefined
    }
  })
}

/**
 * @type {import('./@types').SubscribeType}
 */
export const subscribe = () => {
  window.__GDM_ANALYTICS_LISTENER__ =
    window.__GDM_ANALYTICS_LISTENER__ ||
    (evt => {
      window.__GDM_EVENTS__ = window.__GDM_EVENTS__ || {}
      const target = evt.target.closest('[data-evt-name]:not(.prevent-event)')
      if (!target) {
        return
      }

      const id = target?.dataset?.evtId
      let event

      if (window.__GDM_EVENTS__[id]) {
        event = window.__GDM_EVENTS__[id]
        // We add transactionId only in clientSide events because there won't be clickouts in RSC
        if (event.name && event.name.includes('clickout')) {
          event.type = evt.button || 0
          event.transactionId = uuidv4()
        }
      } else if (target.dataset.evtFallback) {
        event = JSON.parse(atob(target.dataset.evtFallback))
      } else {
        event = parseDataset(target.dataset)
      }

      detachTask(sendEvent, event)
    })
  if (window.__GDM_ANALYTICS_INIT__ === 1) {
    window.addEventListener('click', window.__GDM_ANALYTICS_LISTENER__)
  }
  return () => {
    if (window.__GDM_ANALYTICS_INIT__ > 0) {
      return
    }
    window.removeEventListener('click', window.__GDM_ANALYTICS_LISTENER__)
  }
}

export const init = props => {
  if (typeof window === 'undefined') {
    return () => {
      return null
    }
  }
  window.__GDM_ANALYTICS_INIT__ = window.__GDM_ANALYTICS_INIT__ || 0
  window.__GDM_ANALYTICS_INIT__ += 1

  const unsubcribes = Object.values(services)
    .map(service => {
      return service.init(props)
    })
    .concat([subscribe()])

  return () => {
    window.__GDM_ANALYTICS_INIT__ -= 1
    unsubcribes.forEach(unsubscribe => {
      if (typeof unsubscribe === 'function') {
        unsubscribe()
      }
    })
  }
}
